import { parseProviderScenario, parseScenario, TITLE_SEPARATOR } from './test-key.js';
import type { ExternalTestProvider } from '@chatsdet/types';

export type ExternalStepVerdict = 'passed' | 'failed' | 'flaky' | 'skipped';

export type ExternalScenarioItem = {
  id: string;
  testKey: string;
  displayName: string;
  status: 'passed' | 'failed' | 'skipped';
  attempt: number;
  orderIndex: number | null;
  durationMs: number | null;
};

export type ExternalScenarioStep<T extends ExternalScenarioItem = ExternalScenarioItem> = T & {
  sectionLabel: string | null;
  stepLabel: string;
  verdict: ExternalStepVerdict;
  attempts: number;
};

export type ExternalScenarioSection<T extends ExternalScenarioItem = ExternalScenarioItem> = {
  label: string | null;
  steps: ExternalScenarioStep<T>[];
};

export type ExternalScenario<T extends ExternalScenarioItem = ExternalScenarioItem> = {
  key: string;
  label: string;
  sections: ExternalScenarioSection<T>[];
  total: number;
  passed: number;
  failed: number;
  flaky: number;
  skipped: number;
  firstOrder: number;
  steps: ExternalScenarioStep<T>[];
  verdict: ExternalStepVerdict;
  durationMs: number | null;
  firstFailure: ExternalScenarioStep<T> | null;
  routeItemId: string;
};

export type ExternalSkipProvenance = {
  kind: 'filtered_by_selection' | 'reported_skipped_unknown';
  explanation: string;
};

function parseStep(testKey: string, displayName: string) {
  const scenario = parseScenario(testKey, displayName);
  const hashIdx = testKey.indexOf('#');
  const afterHash = hashIdx >= 0 ? testKey.slice(hashIdx + 1) : displayName;
  const path = afterHash
    .split(TITLE_SEPARATOR)
    .map((segment) => segment.trim())
    .filter(Boolean);
  const leaf = path.at(-1) ?? displayName;
  const sectionSegment = path.find((segment) => /^SECTION:/i.test(segment));
  const sectionLabel = sectionSegment
    ? sectionSegment.replace(/^SECTION:\s*/i, '').trim()
    : path.length >= 3
      ? (path.at(-2) ?? null)
      : null;
  return {
    specFile: scenario.specFile,
    scenarioLabel: scenario.label,
    sectionLabel,
    stepLabel: leaf,
  };
}

/** Fold all attempts for one test before rolling the test into a scenario. */
export function foldExternalStepVerdict(
  statuses: readonly ExternalScenarioItem['status'][]
): ExternalStepVerdict {
  const anyPass = statuses.includes('passed');
  const anyFail = statuses.includes('failed');
  if (anyFail && anyPass) return 'flaky';
  if (anyFail) return 'failed';
  if (anyPass) return 'passed';
  return 'skipped';
}

/**
 * Group flat provider rows into logical scenarios while folding retry rows.
 * This is the canonical grouping used by the web UI and customer diagnostics.
 */
export function groupExternalScenarios<T extends ExternalScenarioItem>(
  items: readonly T[],
  provider: ExternalTestProvider
): ExternalScenario<T>[] {
  const statusesByTest = new Map<string, ExternalScenarioItem['status'][]>();
  const latestByTest = new Map<string, T>();
  for (const item of items) {
    const statuses = statusesByTest.get(item.testKey) ?? [];
    statuses.push(item.status);
    statusesByTest.set(item.testKey, statuses);
    const latest = latestByTest.get(item.testKey);
    if (!latest || item.attempt > latest.attempt) latestByTest.set(item.testKey, item);
  }

  const ordered: T[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    if (seen.has(item.testKey)) continue;
    seen.add(item.testKey);
    const latest = latestByTest.get(item.testKey);
    if (latest) ordered.push(latest);
  }

  const grouped = new Map<string, { label: string; steps: ExternalScenarioStep<T>[] }>();
  for (const item of ordered) {
    const parsed = parseStep(item.testKey, item.displayName);
    const boundary = parseProviderScenario(provider, item.testKey, item.displayName);
    const key = boundary.key;
    const scenario = grouped.get(key) ?? { label: boundary.label, steps: [] };
    scenario.steps.push({
      ...item,
      sectionLabel: parsed.sectionLabel,
      stepLabel: parsed.stepLabel,
      verdict: foldExternalStepVerdict(statusesByTest.get(item.testKey) ?? [item.status]),
      attempts: statusesByTest.get(item.testKey)?.length ?? 1,
    });
    grouped.set(key, scenario);
  }

  const scenarios: ExternalScenario<T>[] = [];
  for (const [key, { label, steps }] of grouped) {
    const sections: ExternalScenarioSection<T>[] = [];
    for (const step of steps) {
      const previous = sections.at(-1);
      if (!previous || previous.label !== step.sectionLabel) {
        sections.push({ label: step.sectionLabel, steps: [step] });
      } else {
        previous.steps.push(step);
      }
    }

    const counts = { passed: 0, failed: 0, flaky: 0, skipped: 0 };
    for (const step of steps) counts[step.verdict] += 1;
    const verdict: ExternalStepVerdict =
      counts.failed > 0
        ? 'failed'
        : counts.flaky > 0
          ? 'flaky'
          : counts.passed > 0
            ? 'passed'
            : 'skipped';
    const timed = steps.filter((step) => step.durationMs != null);
    const first = steps[0];
    if (!first) continue;
    scenarios.push({
      key,
      label,
      sections,
      total: steps.length,
      ...counts,
      firstOrder: steps.reduce(
        (minimum, step) => Math.min(minimum, step.orderIndex ?? Number.MAX_SAFE_INTEGER),
        Number.MAX_SAFE_INTEGER
      ),
      steps,
      verdict,
      durationMs: timed.length
        ? timed.reduce((total, step) => total + (step.durationMs ?? 0), 0)
        : null,
      firstFailure:
        steps.find((step) => step.verdict === 'failed' || step.verdict === 'flaky') ?? null,
      routeItemId: first.id,
    });
  }
  return scenarios.sort((left, right) => left.firstOrder - right.firstOrder);
}

function testKeyGrepTitle(testKey: string): string {
  const hash = testKey.indexOf('#');
  const title = hash === -1 ? testKey : testKey.slice(hash + 1);
  return title.split(TITLE_SEPARATOR).join(' ');
}

/**
 * Explain only what the retained data proves about a skipped row. An explicit
 * grep mismatch is attributable to selection; all other skips stay unknown
 * because provider reports do not preserve `.skip`, hook, or harness intent.
 */
export function explainExternalSkip(
  item: Pick<ExternalScenarioItem, 'status' | 'testKey'>,
  selection: { grep?: string | null }
): ExternalSkipProvenance | null {
  if (item.status !== 'skipped') return null;
  if (selection.grep) {
    try {
      if (!new RegExp(selection.grep).test(testKeyGrepTitle(item.testKey))) {
        return {
          kind: 'filtered_by_selection',
          explanation: `The recorded --grep selection does not match this test's provider title.`,
        };
      }
    } catch {
      // The run accepted the provider expression. If it is not portable to the
      // current JS runtime, do not turn that ambiguity into a false claim.
    }
  }
  return {
    kind: 'reported_skipped_unknown',
    explanation:
      'The provider reported this test as skipped, but retained results do not distinguish an explicit skip from a hook or harness decision.',
  };
}
